import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { isDefined } from '../../helpers/common.helper';
import { AdvancedSearchAddMode, AdvancedSearchControl, AdvancedSearchControlType } from '../../../../interfaces/advancedSearch.interface';

@Component({
    selector: 'app-advanced-search',
    templateUrl: './advanced-search.component.html',
    styleUrls: ['./advanced-search.component.scss']
})
export class AdvancedSearchComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @Input() isMainSearch = false;
    @Input() control: FormControl;
    @Input() disabled = false;
    @Input() hasFilters = false;
    @Input() openFilters = false;
    @Input() hideSearchButton = false;

    @Input() mainFilterControls: AdvancedSearchControl[] = [];
    @Input() optionalFilterControls: AdvancedSearchControl[] = [];

    @Input() hasSuggestor = false;
    @Input() suggestorItemTemplate: TemplateRef<any>;
    @Input() suggestPool: any[] = [];   // need to improve
    @Input() suggestOptionField: string;


    @Output('search') searchEvEm = new EventEmitter();
    @Output('filtersClosed') filtersClosedEvEm = new EventEmitter<boolean>();

    @Output('suggest') suggestEvEm = new EventEmitter();
    @Output('suggestionSelected') suggestionSelectedEvEm = new EventEmitter();

    @Output('mainFiltersArranged') mainFiltersArrangedEvEm = new EventEmitter();

    @ViewChild('defaultItemTempl', { static: false }) defaultItemTemplRef: TemplateRef<any>;

    isFilterCollapsed: boolean;

    filterControls: AdvancedSearchControl[] = [];
    availableFilterControls: AdvancedSearchControl[] = [];

    $search = new Subject<string>();
    searchSubscription = Subscription.EMPTY;

    $suggestPool = new BehaviorSubject<any[]>([]);
    suggestPool$ = this.$suggestPool.asObservable();

    readonly searchTypes = AdvancedSearchControlType;

    isViewInitialized = false;

    constructor() { }

    ngOnInit(): void {
        if (!this.control) {
            this.control = new FormControl();
        }
        this.isFilterCollapsed = isDefined(this.openFilters) ? !this.openFilters : !this.hasActiveFilters;
    }

    ngAfterViewInit(): void {
        this.isViewInitialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes) {
            if (changes.openFilters) {
                this.isFilterCollapsed = !changes.openFilters.currentValue;
            }

            if (this.hasSuggestor && changes.suggestorItemTemplate && !!this.suggestorItemTemplate) {
                this.searchSubscription.unsubscribe();
                this.searchSubscription = this.$search.asObservable().pipe(
                    debounceTime(500),
                    distinctUntilChanged(),
                ).subscribe(
                    searchTerm => {
                        this.suggestEvEm.emit(searchTerm);
                        // console.log('Emitting suggestor for term: ', searchTerm);
                    }
                );
            }

            if (changes.suggestPool) {
                this.$suggestPool.next(changes.suggestPool.currentValue || []);
            }

            if ((changes.mainFilterControls || changes.optionalFilterControls) &&
                (changes.mainFilterControls.currentValue && changes.optionalFilterControls.currentValue)) {
                this.filterControls = changes.mainFilterControls.currentValue;
                if (changes.optionalFilterControls.currentValue) {
                    (changes.optionalFilterControls.currentValue as AdvancedSearchControl[]).forEach(filter => {
                        this._arrangeFilterByValueExistance(filter);
                    });
                    this.mainFiltersArrangedEvEm.emit();
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.searchSubscription.unsubscribe();
    }

    private _arrangeFilterByValueExistance(filter: AdvancedSearchControl, parentFilter?: AdvancedSearchControl) {
        if (filter.subMenu) {
            filter.subMenu.forEach((subFilter, sFi) => {
                this._arrangeFilterByValueExistance(subFilter, filter);
            });
        } else {
            const ctrlVal = filter.control.value;
            let addToAvailableFilters = true;
            if (isDefined(ctrlVal, true)) {
                if (filter.addMode === AdvancedSearchAddMode.infinite) {
                    let nextQsNameIndex = this.filterControls.filter(fc => fc.id === filter.id).length;
                    (ctrlVal as string[]).forEach((s, sI) => {
                        this.filterControls.push(
                            {
                                ...filter,
                                control: new FormControl(s),
                                qsName: `${filter.qsName}${nextQsNameIndex++}`
                            }
                        );
                    });
                    // push a copy with subData
                    filter.control.patchValue([]);
                    addToAvailableFilters = true;
                } else {
                    this.filterControls.push(filter);
                    addToAvailableFilters = false;
                }
            }
            if (addToAvailableFilters) {
                if (parentFilter) {
                    let parentIndex = -1;
                    if (!this.availableFilterControls.some((x, xI) => {
                        if (x.id === parentFilter.id) {
                            parentIndex = xI;
                            return true;
                        }
                        return false;
                    })) {
                        // parent filter not found in available filters. Let's add it
                        this.availableFilterControls.push({
                            ...parentFilter,
                            control: parentFilter.control,
                            subMenu: []
                        });
                        parentIndex = this.availableFilterControls.length - 1;
                    }

                    this.availableFilterControls[parentIndex].subMenu.push(filter);

                } else {
                    this.availableFilterControls.push(filter);
                }
            }
        }

    }

    handleSearch() {
        if (this.disabled) {
            return;
        }
        const searchText = this.control.value;
        this.searchEvEm.emit(searchText);
    }

    toggleFilterCollapsed() {
        this.isFilterCollapsed = !this.isFilterCollapsed;
        this.filtersClosedEvEm.emit(this.isFilterCollapsed);
    }

    onSearchInput(searchValue: string) {
        this.$search.next(searchValue);
    }

    onSuggestionSelect(event) {
        this.control.patchValue(event.item.displayName);
        this.suggestionSelectedEvEm.emit(event.item.item);
    }

    getItemTemplate() {
        return this.suggestorItemTemplate || null; // fallback to the default
    }

    onAdditionalFilterAdd(filterToAdd: AdvancedSearchControl) {
        this._addToMainFilters(this.availableFilterControls, filterToAdd);
    }

    private _addToMainFilters(searchPool: AdvancedSearchControl[], filterToAdd: AdvancedSearchControl, options?) {
        const foundIndex = searchPool.findIndex(x => x.id === filterToAdd.id);
        if (foundIndex > -1) {
            let filtersToMove: AdvancedSearchControl[] = [];
            if (filterToAdd.addMode === AdvancedSearchAddMode.infinite) {
                const nextQsNameIndex = this.filterControls.filter(fc => fc.id === filterToAdd.id).length;
                filtersToMove.push(
                    {
                        ...searchPool[foundIndex],
                        control: new FormControl(),
                        qsName: `${searchPool[foundIndex].qsName}${nextQsNameIndex}`
                    }
                );
            } else {
                filtersToMove = searchPool.splice(foundIndex, 1);
            }

            this.filterControls.push(...filtersToMove);
        } else {
            searchPool.filter(x => x.subMenu && x.subMenu.length > 0).forEach(sub => {
                this._addToMainFilters(sub.subMenu, filterToAdd, options);
            });
        }
    }

    onResetAllFilters() {
        this.filterControls.forEach(el => {
            el.control.patchValue(null);
            el.control.updateValueAndValidity();
        });
    }

    onControlValueChanged(ctrl: AdvancedSearchControl) {

    }

    private _searchFilters(pool: AdvancedSearchControl[], id: string) {
        pool.filter(x => {
            if (x.subMenu && x.subMenu.length > 0) {
                return this._searchFilters(x.subMenu, id);
            }
            return x.id === id;
        });
    }

    get hasActiveFilters() {
        let found = false;
        if (!this.filterControls) {
            return false;
        }
        found = this.filterControls.some(el => {
            const elValue = el.control.value;
            if (el.type === AdvancedSearchControlType.toggle) {
                if (elValue === true) {
                    return true;
                }
            } else {
                if (isDefined(elValue, true)) {
                    return true;
                }
            }
            return false;
        });

        return found;
    }

    get hasAddableOptions() {
        let retValue = false;
        if (this.availableFilterControls) {
            retValue = this.availableFilterControls.some(x => (x.subMenu && x.subMenu.length > 0) || !x.subMenu);
        }
        return retValue;
    }

    get isSuggestorEnabled() { return !!this.hasSuggestor; }
    get hasSuggestorTemplate() { return !!this.suggestorItemTemplate; }
}
